import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Hash, Lock, Search, UserPlus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChannelDialog = ({ open, onClose }) => {
  const { activeTeam, loadChannels } = useChat();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');

  useEffect(() => {
    if (open && isPrivate) {
      axios.get(`${API}/users`).then(res => setAllUsers(res.data.users)).catch(() => {});
    }
  }, [open, isPrivate]);

  const toggleMember = (userId) => {
    setSelectedMembers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!activeTeam) { toast.error('No team selected'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/channels`, {
        name,
        description,
        is_private: isPrivate,
        team_id: activeTeam.team_id,
        member_ids: isPrivate ? selectedMembers : []
      });
      toast.success(`Channel #${name} created`);
      loadChannels(activeTeam.team_id);
      setName('');
      setDescription('');
      setIsPrivate(false);
      setSelectedMembers([]);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create channel');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = allUsers.filter(u =>
    (u.display_name || u.username || '').toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="create-channel-dialog">
        <DialogHeader>
          <DialogTitle style={{fontFamily: 'Space Grotesk'}}>Create Channel</DialogTitle>
          <DialogDescription>Create a new channel for your team to collaborate.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="space-y-2">
            <Label>Channel Name</Label>
            <Input placeholder="e.g. project-updates" value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '-'))} required data-testid="channel-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input placeholder="What is this channel about?" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="channel-description-input" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Private Channel</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Only invited members can access</p>
            </div>
            <Switch checked={isPrivate} onCheckedChange={setIsPrivate} data-testid="channel-private-switch" />
          </div>

          {/* Member selection for private channels */}
          {isPrivate && (
            <div className="space-y-2" data-testid="channel-member-picker">
              <Label className="flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5" /> Add Members</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search users..." className="pl-9 h-8 text-sm" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} data-testid="channel-member-search" />
              </div>
              {selectedMembers.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedMembers.map(id => {
                    const u = allUsers.find(u => u.user_id === id);
                    return u ? (
                      <Badge key={id} variant="secondary" className="text-xs cursor-pointer hover:bg-destructive/20" onClick={() => toggleMember(id)} data-testid={`selected-member-${id}`}>
                        {u.display_name || u.username} &times;
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
              <ScrollArea className="max-h-36 border border-border rounded-lg">
                <div className="p-1 space-y-0.5">
                  {filteredUsers.map(u => (
                    <button
                      key={u.user_id}
                      type="button"
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${selectedMembers.includes(u.user_id) ? 'bg-[hsl(var(--primary)/0.10)] text-foreground' : 'hover:bg-[hsl(var(--surface-2))]'}`}
                      onClick={() => toggleMember(u.user_id)}
                      data-testid={`member-option-${u.user_id}`}
                    >
                      <div className="w-6 h-6 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-[10px] font-semibold">
                        {u.display_name?.[0] || u.username?.[0]}
                      </div>
                      <span className="flex-1 text-left truncate">{u.display_name || u.username}</span>
                      {selectedMembers.includes(u.user_id) && <div className="w-2 h-2 rounded-full bg-[hsl(var(--primary))]" />}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="channel-create-submit">
              {loading ? 'Creating...' : 'Create Channel'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChannelDialog;